import {useState, useEffect, useRef} from "react";
import Hls from "hls.js";

import {Calendar, X} from "lucide-react";

const BASE_URL = process.env.REACT_APP_AWS_USER_ENDPOINT;
const AUTH_HEADER = `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`;
const HEADERS = {
  "Content-Type": "application/json",
  Authorization: AUTH_HEADER,
};

// --- Utilities ---
const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${hours > 0 ? hours + "h " : ""}${minutes > 0 ? minutes + "m " : ""}${seconds}s`;
};

const formatPlaybackTime = (seconds) => {
  if (isNaN(seconds)) return "0:00";

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const getTodayKey = () => new Date().toISOString().split("T")[0];

const apiFetch = async (endpoint, options = {}) => {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {...HEADERS, ...options.headers},
  });
  if (!response.ok)
    throw new Error(
      options.errorMsg || `HTTP error! status: ${response.status}`,
    );
  return response.json();
};

// --- Components ---
const StatBox = ({label, value, sub, color}) => (
  <div className="bg-slate-300/50 border border-slate-300 p-5 rounded-2xl flex flex-col justify-between h-full min-h-[140px] w-full">
    <p className="text-slate-500 text-[10px] sm:text-xs font-bold uppercase tracking-widest leading-tight mb-2">
      {label}
    </p>
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-2 mt-auto">
      <span
        className={`text-xl sm:text-2xl font-mono font-bold ${color} break-all leading-none`}
      >
        {value}
      </span>
      <span className="text-slate-600 text-[10px] sm:text-xs shrink-0 leading-none">
        {sub}
      </span>
    </div>
  </div>
);

const VideoCard = ({
  video,
  savedTime,
  onProgress,
  isActive,
  onPlay,
  isMuted,
  onToggleMute,
  onInteraction,
}) => {
  const videoRef = useRef(null);
  const ytContainerRef = useRef(null);
  const playerRef = useRef(null);
  const lastTimeRef = useRef(savedTime);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(savedTime);
  const [isExpanded, setIsExpanded] = useState(false);

  const isYouTube =
    video.url.includes("youtube.com") || video.url.includes("youtu.be");

  useEffect(() => {
    if (
      isYouTube &&
      playerRef.current &&
      typeof playerRef.current.mute === "function"
    ) {
      isMuted ? playerRef.current.mute() : playerRef.current.unMute();
    } else if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isYouTube]);

  useEffect(() => {
    if (!isActive && !isExpanded) {
      if (isYouTube && playerRef.current?.pauseVideo)
        playerRef.current.pauseVideo();
      else if (videoRef.current) videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isActive, isYouTube, isExpanded]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsExpanded(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    let interval;

    if (isYouTube) {
      const videoId = video.url.includes("v=")
        ? video.url.split("v=")[1].split("&")[0]
        : video.url.split("/").pop().split("?")[0];

      const loadYT = () => {
        if (!window.YT || !window.YT.Player) {
          setTimeout(loadYT, 100);
          return;
        }

        if (playerRef.current) {
          try {
            playerRef.current.destroy();
          } catch (e) {}
          playerRef.current = null;
        }

        playerRef.current = new window.YT.Player(ytContainerRef.current, {
          height: "100%",
          width: "100%",
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            rel: 0,
            modestbranding: 1,
            controls: 0,
            disablekb: 1,
            origin: window.location.origin,
            enablejsapi: 1,
          },
          events: {
            onReady: (e) => {
              setDuration(e.target.getDuration());
              if (savedTime > 0) e.target.seekTo(savedTime, true);
              if (isMuted) e.target.mute();
              e.target.pauseVideo();
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                setIsPlaying(true);
                onPlay(video.id);
              } else if (
                event.data === window.YT.PlayerState.PAUSED ||
                event.data === window.YT.PlayerState.ENDED
              ) {
                setIsPlaying(false);
              }
            },
          },
        });
      };

      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      loadYT();

      interval = setInterval(() => {
        if (
          playerRef.current &&
          typeof playerRef.current.getCurrentTime === "function"
        ) {
          const state = playerRef.current.getPlayerState();
          if (state === 1) {
            const curr = playerRef.current.getCurrentTime();
            setCurrentTime(curr);
            const delta = curr - lastTimeRef.current;
            if (delta > 0 && delta < 2) onProgress(video.id, curr, delta);
            lastTimeRef.current = curr;
          }
        }
      }, 1000);
    } else {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      videoElement.pause();

      if (video.url.includes(".m3u8")) {
        if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(video.url);
          hls.attachMedia(videoElement);
        } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
          videoElement.src = video.url;
        }
      } else {
        videoElement.src = video.url;
      }
    }

    return () => {
      if (interval) clearInterval(interval);
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
    };
  }, [video.id, video.url]);

  const handleTogglePlay = (e) => {
    e.stopPropagation();
    onInteraction();
    if (isYouTube && playerRef.current) {
      isPlaying
        ? playerRef.current.pauseVideo()
        : playerRef.current.playVideo();
    } else if (videoRef.current) {
      isPlaying ? videoRef.current.pause() : videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const handleToggleMute = (e) => {
    e.stopPropagation();
    onInteraction();
    onToggleMute();
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    onInteraction();
    const seekTo = parseFloat(e.target.value);
    if (isYouTube && playerRef.current) playerRef.current.seekTo(seekTo, true);
    else if (videoRef.current) videoRef.current.currentTime = seekTo;
    setCurrentTime(seekTo);
  };

  return (
    <div
      className={`
      group border border-slate-800 overflow-hidden bg-slate-300/50 transition-all flex flex-col
      ${isExpanded ? "fixed inset-0 z-[999] bg-slate-950 p-10" : "relative h-full rounded-2xl"}
    `}
    >
      <div
        className={`relative bg-black overflow-hidden rounded-xl ${isExpanded ? "flex-grow" : "w-full aspect-video"}`}
      >
        <div className="w-full h-full relative">
          {isYouTube ? (
            <div ref={ytContainerRef} className="absolute inset-0" />
          ) : (
            <video
              ref={videoRef}
              autoPlay={false}
              preload="metadata"
              muted={isMuted}
              className="absolute inset-0 w-full h-full object-cover"
              onPlay={() => {
                onPlay(video.id);
                setIsPlaying(true);
              }}
              onPause={() => setIsPlaying(false)}
              onLoadedMetadata={(e) => {
                setDuration(e.target.duration);
                e.target.pause();
              }}
              onTimeUpdate={(e) => {
                const curr = e.target.currentTime;
                setCurrentTime(curr);
                const delta = curr - lastTimeRef.current;
                if (delta > 0 && delta < 1) onProgress(video.id, curr, delta);
                lastTimeRef.current = curr;
              }}
            />
          )}
          <div
            className="absolute inset-0 z-10 cursor-pointer"
            onClick={handleTogglePlay}
          />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-20 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-2">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime || 0}
            onChange={handleSeek}
            className="w-full h-1 accent-orange-500 cursor-pointer"
          />
          <div className="flex items-center justify-between text-white text-xs">
            <div className="flex gap-4 items-center">
              <button
                onClick={handleTogglePlay}
                className="hover:text-orange-400 transition-colors"
              >
                {isPlaying ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
              <button
                onClick={handleToggleMute}
                className="hover:text-orange-400 transition-colors"
              >
                {isMuted ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInteraction();
                  setIsExpanded(!isExpanded);
                }}
                className="hover:text-blue-400 transition-colors"
              >
                {isExpanded ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 14h6v6m10-10h-6V4m6 6l-6-6M4 20l6-6" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  </svg>
                )}
              </button>
            </div>
            <span className="font-mono">
              {formatPlaybackTime(currentTime)} / {formatPlaybackTime(duration)}
            </span>
          </div>
        </div>
      </div>

      <div className={`${isExpanded ? "pt-6" : "p-4"} flex flex-col`}>
        <h3
          className={`font-semibold truncate ${isExpanded ? "text-white text-2xl" : "text-slate-500"}`}
        >
          {video.title}
        </h3>
        <p
          className={`text-xs uppercase tracking-widest ${isExpanded ? "text-orange-400 mt-1" : "text-slate-400"}`}
        >
          {video.category}
        </p>
      </div>
    </div>
  );
};

const VideoDashboard = ({user, handleLogout}) => {
  const [videos, setVideos] = useState([]);
  const [watchHistory, setWatchHistory] = useState({});
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isGlobalMuted, setIsGlobalMuted] = useState(false);

  const [activity, setActivity] = useState({});

  const activityRef = useRef(activity);
  const historyRef = useRef(watchHistory);

  const lastSyncDataRef = useRef({activity: {}, history: {}});
  const lastSyncTimeRef = useRef(new Date());

  useEffect(() => {
    activityRef.current = activity;
  }, [activity]);
  useEffect(() => {
    historyRef.current = watchHistory;
  }, [watchHistory]);

  const updateDailyStat = (key, increment = 1) => {
    const today = getTodayKey();
    setActivity((prev) => {
      const todayStats = prev[today] || {
        mouseMoves: 0,
        clicks: 0,
        secondsActive: 0,
        totalVideoSeconds: 0,
      };
      return {
        ...prev,
        [today]: {
          ...todayStats,
          [key]: (todayStats[key] || 0) + increment,
        },
      };
    });
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const {result, data} = await apiFetch(`/get-videos`, {method: "GET"});
        if (result === "success") {
          data.sort((a, b) => a.id.localeCompare(b.id));
          setVideos(data);
        }
      } catch (err) {
        console.error("Failed to fetch videos", err);
      }
    };
    fetchVideos();
  }, []);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const {result, data} = await apiFetch(
          `/get-contact/id/${user?.customer_id}`,
        );
        if (result === "success") {
          if (data[0]?.activity) {
            setActivity(data[0].activity);
            lastSyncDataRef.current.activity = JSON.parse(
              JSON.stringify(data[0].activity),
            );
          }
          if (data[0]?.watch_history) {
            setWatchHistory(data[0].watch_history);
            lastSyncDataRef.current.history = JSON.parse(
              JSON.stringify(data[0].watch_history),
            );
          }
        }
      } catch (err) {
        console.error("Failed to fetch activity", err);
        if (user?.activity) setActivity(user.activity);
        if (user?.watch_history) setWatchHistory(user.watch_history);
      }
    };
    fetchActivity();
  }, [user]);

  useEffect(() => {
    const sync = async () => {
      const now = new Date();
      const timeSinceLastSync = now - lastSyncTimeRef.current;

      const hasActivityChanged =
        JSON.stringify(activityRef.current) !==
        JSON.stringify(lastSyncDataRef.current.activity);
      const hasHistoryChanged =
        JSON.stringify(historyRef.current) !==
        JSON.stringify(lastSyncDataRef.current.history);

      if (
        hasActivityChanged ||
        hasHistoryChanged ||
        timeSinceLastSync > 30000
      ) {
        try {
          const result = await fetch(
            `${process.env.REACT_APP_AWS_USER_ENDPOINT}/update-contact/activity`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
              },
              body: JSON.stringify({
                customer_id_u: user?.customer_id,
                customer_firstname_u: user?.given_name || "Unknown",
                customer_lastname_u: user?.family_name || "Unknown",
                session_id_u: user?.session_id,
                activity_u: activityRef.current,
                watch_history_u: historyRef.current,
                timestamp_u: now.toISOString(),
              }),
            },
          );
          const data = await result.json();

          lastSyncTimeRef.current = now;
          lastSyncDataRef.current.activity = JSON.parse(
            JSON.stringify(activityRef.current),
          );
          lastSyncDataRef.current.history = JSON.parse(
            JSON.stringify(historyRef.current),
          );

          if (data?.updatedAttributes?.session_id !== user?.session_id)
            handleLogout();
        } catch (err) {
          console.error("Sync failed", err);
        }
      }
    };

    const timer = setInterval(() => sync(), 10000);
    return () => clearInterval(timer);
  }, [user, handleLogout]);

  useEffect(() => {
    const onMove = () => updateDailyStat("mouseMoves");
    const onClick = () => updateDailyStat("clicks");
    window.addEventListener("mousemove", onMove, {passive: true});
    window.addEventListener("click", onClick);
    const timer = setInterval(() => {
      if (!document.hidden) updateDailyStat("secondsActive");
    }, 1000);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("click", onClick);
      clearInterval(timer);
    };
  }, []);

  const handleVideoProgress = (id, currentTime, delta) => {
    const today = getTodayKey();
    setWatchHistory((p) => {
      const videoEntry = p[id] || {lastPosition: 0, dailyBreakdown: {}};
      return {
        ...p,
        [id]: {
          lastPosition: currentTime,
          dailyBreakdown: {
            ...videoEntry.dailyBreakdown,
            [today]: (videoEntry.dailyBreakdown[today] || 0) + (delta || 0),
          },
        },
      };
    });
    if (delta) updateDailyStat("totalVideoSeconds", delta);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const filtered = videos.filter(
    (v) =>
      v.title?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (selectedCategory === "All" || v.category === selectedCategory),
  );

  const getAggregatedStats = () => {
    const totals = {
      mouseMoves: 0,
      clicks: 0,
      secondsActive: 0,
      totalVideoSeconds: 0,
    };
    Object.entries(activity).forEach(([date, stats]) => {
      const isInRange =
        (!startDate || date >= startDate) && (!endDate || date <= endDate);
      if (isInRange) {
        totals.mouseMoves += stats.mouseMoves || 0;
        totals.clicks += stats.clicks || 0;
        totals.secondsActive += stats.secondsActive || 0;
        totals.totalVideoSeconds += stats.totalVideoSeconds || 0;
      }
    });
    return totals;
  };

  const displayStats = getAggregatedStats();
  const isFiltering = startDate || endDate;

  return (
    <div className="h-full w-full p-8 font-sans">
      <div className="w-full overflow-hidden bg-blue-50 border-y border-blue-100 shadow-sm mb-4 rounded-[1rem]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap md:flex-nowrap">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="font-medium text-blue-900 space-x-2">
                  <span className="font-bold">Benefits Requirement:</span>
                  <span className="block sm:inline">
                    Members must report X hours per month. This platform tracks
                    your hours automatically.
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-4 md:mt-0 w-full md:w-auto flex-shrink-0">
              <a
                href="https://communityconnectlabs.com/health-plans-work-requirements/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors"
              >
                Learn more
                <svg
                  className="ml-2 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <header className="mb-10 p-1 rounded-[2rem] bg-gradient-to-b from-gray-50 to-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/50">
        <div className="p-6 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Stacked rows for mobile view:
                flex-col on mobile, sm:flex-row on screens >= 640px 
            */}
            <div className="flex flex-col sm:flex-row items-center gap-3 bg-gray-100/50 p-2 sm:p-1.5 rounded-xl border border-gray-200/50 w-full sm:w-auto min-w-fit">
              {/* Row 1 (Start Date) */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="flex items-center gap-2 pl-3 pr-1 text-gray-500">
                  <Calendar size={15} strokeWidth={2.5} />
                </div>
                <input
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent text-sm font-medium text-gray-600 focus:outline-none flex-1 min-w-[140px] pr-8"
                />
              </div>

              {/* Divider - hidden on mobile rows */}
              <div className="hidden sm:block h-4 w-[1px] bg-gray-300 mx-1 shrink-0" />

              {/* Row 2 (End Date + Clear button) */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="flex items-center gap-2 pl-3 pr-1 text-gray-500">
                  <Calendar size={15} strokeWidth={2.5} />
                </div>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent text-sm font-medium text-gray-600 focus:outline-none flex-1 min-w-[140px] pr-8 sm:pl-1"
                />
                {(startDate || endDate) && (
                  <button
                    onClick={clearFilters}
                    className="p-1.5 hover:bg-white rounded-lg text-gray-400 hover:text-red-500 transition-all shadow-sm shrink-0"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div
              className={`${isFiltering ? "bg-yellow-200/50" : "bg-green-200/50"} text-[12px] font-bold uppercase tracking-widest text-gray-600 bg-gray-100/80 px-3 py-1 rounded-full`}
            >
              {isFiltering ? "Filtered View" : "Live Overview"}
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 items-stretch">
            <StatBox
              label="Clicks"
              value={displayStats.clicks}
              sub="Total"
              color="text-rose-500"
            />
            <StatBox
              label="Interactions"
              value={displayStats.mouseMoves.toLocaleString()}
              sub="Moves"
              color="text-indigo-500"
            />
            <StatBox
              label="Platform Time"
              value={formatTime(displayStats.secondsActive)}
              sub="Active"
              color="text-emerald-500"
            />
            <StatBox
              label="Hours Reported (Playtime)"
              value={formatTime(displayStats.totalVideoSeconds)}
              sub="Videos"
              color="text-amber-500"
            />
          </div>
        </div>
      </header>

      <main className="mb-10 space-y-3">
        <div className="flex flex-col gap-6 p-4 bg-slate-300/50 rounded-2xl border border-slate-300">
          <div className="flex flex-wrap gap-2">
            {["All", ...new Set(videos.map((v) => v.category))].map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                  selectedCategory === cat
                    ? "bg-orange-500 border-orange-600 text-white shadow-sm"
                    : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
          {filtered.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              savedTime={watchHistory[video.id]?.lastPosition || 0}
              onProgress={handleVideoProgress}
              isActive={activeVideoId === video.id}
              onPlay={setActiveVideoId}
              isMuted={isGlobalMuted}
              onToggleMute={() => setIsGlobalMuted(!isGlobalMuted)}
              onInteraction={() => updateDailyStat("clicks")}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default VideoDashboard;
